import Service from '@ember/service';
import { service } from '@ember/service';
import type StoreService from 'scaleflow/services/store';
import type CurrentUserService from 'scaleflow/services/current-user';
import type RouterService from '@ember/routing/router-service';
import type SessionService from 'scaleflow/services/session';
import type OrganisationService from 'scaleflow/services/organisation';

import type Agent from 'scaleflow/models/agent';
import { tracked } from '@glimmer/tracking';
import { DateTime } from 'scaleflow/utils/date-time';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import type ConversationModel from 'scaleflow/models/conversation';
import type InitiativeModel from 'scaleflow/models/initiative';
import type MessageModel from 'scaleflow/models/message';
import capitalize from 'scaleflow/utils/capitalize';

export default class Chatv2Service extends Service {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare currentUser: CurrentUserService;
  @service declare organisation: OrganisationService;
  @service declare session: SessionService;

  @tracked activeConversation = '';
  @tracked chatWindowIsOpen = false;
  @tracked recentChatsIsVisible = false;

  get messageAdapter() {
    return this.store.adapterFor('message');
  }

  setupCalled: boolean = false;

  setup() {
    this.router.on('routeDidChange', this.routeDidChangeHandler);
    void this.store.findAll('agent');
    void this.store.findAll('conversation');
  }

  routeDidChangeHandler = () => {
    this.setupCalled = true;
    // void this.populateChatWindow();
  };

  get activeConversationRecord() {
    if (!this.activeConversation) return null;
    return this.store.peekRecord(
      'conversation',
      this.activeConversation,
    ) as ConversationModel;
  }

  setConversationLoading(conversation_id: string, isComplete: boolean = true) {
    const conversationRecord = this.store.peekRecord(
      'conversation',
      conversation_id,
    ) as ConversationModel;
    conversationRecord.complete = isComplete;
  }

  get isInitiativePage() {
    return this.router.currentRoute?.name?.includes('initiatives.initiative');
  }

  get currentInitiative() {
    if (!this.isInitiativePage) return {};
    else {
      return (this.router as Record<string, any>)['currentRoute']?.parent
        ?.attributes;
    }
  }

  async saveMessage(userMessage: string, conversation_id: string) {
    //Persist locally
    const _message = this.store.createRecord('message', {
      text: userMessage,
      type: 'USER',
      created_at: new Date().toISOString(),
      conversation: this.store.peekRecord('conversation', conversation_id),
      created_by: { type: 'PERSON', id: this.currentUser.id },
    }) as MessageModel;

    await _message.save();
  }

  sendMessage(message: string, conversation_id: string) {
    // Set Conversation to loading
    this.setConversationLoading(conversation_id, false);
    void this.saveMessage(message, conversation_id);
  }

  populateChatWindow = async () => {
    if (!this.currentInitiative?.id) {
      return;
    }

    const initiative = this.store.peekRecord(
      'initiative',
      this.currentInitiative.id as string,
    ) as InitiativeModel;
    const conversations = await initiative.conversations;
    if (conversations.length === 0) return;
    else {
      const latestConversation =
        initiative.latestConversation as ConversationModel;
      await this.restoreConversation(latestConversation);
    }
  };

  async restoreConversation(conversation: ConversationModel) {
    if (!conversation) assert('Conversation is undefined', true);

    //If already active conversation, do nothing
    if (this.activeConversation === conversation.id) return;

    //If initiative does not exist, return
    const initiative = conversation.initiative.content;
    if (!initiative) assert('Initiative is undefined', true);

    //Else load messages
    this.activeConversation = conversation.id as string;
    await conversation.messages;
  }

  @action
  toggleRecentChatsIsVisible(val?: boolean) {
    if (val === undefined)
      this.recentChatsIsVisible = !this.recentChatsIsVisible;
    else this.recentChatsIsVisible = val;
  }

  @action
  toggleChatWindow(val?: boolean) {
    if (val === undefined) this.chatWindowIsOpen = !this.chatWindowIsOpen;
    else this.chatWindowIsOpen = Boolean(val);
  }

  async initiateConversation() {
    const initiative = this.isInitiativePage
      ? this.currentInitiative
      : undefined;
    // if (!initiative?.id) return;

    const conversation = this.store.createRecord('conversation', {
      title: `${initiative?.title ?? 'New chat'} - ${DateTime.now().format('DD/MM/YYYY HH:mm')}`,
      provider: 'n8n',
      config: {
        url: '',
        payload: {},
      },
      initiative: initiative ?? undefined,
      created_at: new Date().toISOString(),
    }) as ConversationModel;

    await conversation.save();

    this.activeConversation = conversation.id as string;
  }

  async fillConversationDetails(conversation_id: string, agent: Agent) {
    const activeConversation = conversation_id;
    const conversation = this.store.peekRecord(
      'conversation',
      activeConversation,
    ) as ConversationModel;
    if (conversation.initiative.content) {
      const initiative_id = conversation.initiative.id;
      const initiative = this.store.peekRecord(
        'initiative',
        initiative_id!,
      ) as InitiativeModel;
      conversation.initiative =
        initiative as unknown as ConversationModel['initiative'];
    }
    conversation.title = `${agent.name} - ${DateTime.now().format('DD/MM/YYYY HH:mm')}`;
    conversation.setAgentOnConversation(agent);

    await conversation.save();
    return conversation;
  }

  populateMessageFieldWithQuery(query: string) {
    const textBox = document.querySelector('#chat-form #query');
    if (textBox) textBox.textContent = query;
  }

  async handleConversationInitiateWithAgent(agent: Agent) {
    if (!this.activeConversation) await this.initiateConversation();
    const conversation = await this.fillConversationDetails(
      this.activeConversation,
      agent,
    );
    const query = agent.config['query'] as string;
    if (conversation?.initiative?.id) {
      this.sendMessage(query, this.activeConversation);
    } else {
      this.populateMessageFieldWithQuery(query);
    }
  }

  stopConversation() {
    const messages = this.activeConversationRecord!.sortedMessages;
    const lastBotMessage = messages
      .filter((m: MessageModel) => m.type === 'BOT')
      .pop();
    if (!lastBotMessage) return;
    lastBotMessage.status = 'STOPPED';
    void lastBotMessage.save();
  }

  unsetActiveConversation() {
    this.activeConversation = '';
  }

  get payloadDictionary() {
    return {
      $access_token: this.session.accessToken,
      $initiative_id:
        this.currentInitiative.id ||
        this.activeConversationRecord!.initiative.content,
      $tenant_id: this.organisation.id,
      $user_id: this.currentUser.id,
    };
  }

  constructPayload(
    payloadConfig: Record<string, any>,
    payloadDictionary: Record<string, any>,
  ) {
    const derived_custom_payload = Object.entries(payloadConfig).reduce<
      Record<string, string>
    >((acc, [key, value]) => {
      const str_val = String(value);
      if (str_val.startsWith('$')) {
        acc[key] = payloadDictionary[value] ?? '';
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
    return derived_custom_payload;
  }

  validateConversation(conversation: ConversationModel): string[] {
    const config = conversation.config.payload as Record<string, any>;
    const payload = this.constructPayload(config, this.payloadDictionary);
    const errors: string[] = [];
    Object.entries(payload).forEach(([key, value]) => {
      if (!value)
        errors.push(
          `${capitalize(key.replace('$', '').replace('_', ' '))} is missing`,
        );
    });
    return errors;
  }

  willDestroy(): void {
    if (this.setupCalled)
      this.router.off('routeDidChange', this.routeDidChangeHandler);
    super.willDestroy();
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:chatv2')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('chatv2') declare altName: Chatv2Service;`.
declare module '@ember/service' {
  interface Registry {
    chatv2: Chatv2Service;
  }
}
